import Image from "@tiptap/extension-image";
import { mergeAttributes, type NodeViewRendererProps } from "@tiptap/core";

export const CustomImage = Image.extend({
  inline: false,

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: "100%",
        renderHTML: (attributes) => ({
          style: `width: ${attributes.width}`,
        }),
        parseHTML: (element) => element.style.width || element.getAttribute("width") || "100%",
      },
      align: {
        default: "center",
        renderHTML: (attributes) => ({
          "data-align": attributes.align,
        }),
        parseHTML: (element) => element.getAttribute("data-align") || "center",
      },
      wrap: {
        default: false,
        renderHTML: (attributes) => ({
          "data-wrap": attributes.wrap,
        }),
        parseHTML: (element) => {
          // getAttribute returns string | null, so a `=== true` check can never
          // match — compare against the string form only.
          return element.getAttribute("data-wrap") === "true";
        },
      },
      margin: {
        default: "0",
        renderHTML: (attributes) => ({
          style: `margin: ${attributes.margin}`,
        }),
        parseHTML: (element) => element.style.margin || element.getAttribute("data-margin") || "0",
      },
    };
  },

  addNodeView() {
    return ({ node, HTMLAttributes, getPos, editor }: NodeViewRendererProps) => {
      const container = document.createElement("div");
      container.setAttribute("data-image-wrapper", "");
      container.setAttribute("data-align", node.attrs.align);
      container.setAttribute("data-wrap", node.attrs.wrap);
      // Added studio-image-wrapper for robust CSS targeting
      container.className = "image-node-view studio-image-wrapper";

      const imgContainer = document.createElement("div");
      // Ensure classes are fully qualified for Tailwind
      imgContainer.className = "relative inline-block studio-editor-image group";
      imgContainer.style.width = node.attrs.width;

      // Apply margin to the container (imgContainer) so handles move WITH the image
      imgContainer.style.margin = node.attrs.margin || "0";

      const img = document.createElement("img");
      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        if (key !== "style" && key !== "class") {
          img.setAttribute(key, value as string);
        }
      });
      img.classList.add(
        "w-full",
        "h-auto",
        "block",
        "rounded-lg",
        "transition-shadow",
        "duration-200",
      );
      img.style.setProperty("margin", "0", "important"); // FORCE no margin on img

      imgContainer.appendChild(img);

      // Resizing Handles (Professional Hit-Areas)
      if (editor.isEditable) {
        const createHandle = (position: string) => {
          const handleWrapper = document.createElement("div");
          // Huge invisible hit area for better UX
          handleWrapper.className = `absolute w-6 h-6 z-20 flex items-center justify-center opacity-0 transition-opacity pointer-events-auto`;

          const handleVisual = document.createElement("div");
          handleVisual.className =
            "w-3 h-3 bg-primary rounded-full border-2 border-white shadow-md";

          let cursor = "nwse-resize";

          switch (position) {
            case "top-left":
              handleWrapper.className += " -top-3 -left-3";
              cursor = "nwse-resize";
              break;
            case "top-right":
              handleWrapper.className += " -top-3 -right-3";
              cursor = "nesw-resize";
              break;
            case "bottom-left":
              handleWrapper.className += " -bottom-3 -left-3";
              cursor = "nesw-resize";
              break;
            case "bottom-right":
              handleWrapper.className += " -bottom-3 -right-3";
              cursor = "nwse-resize";
              break;
          }

          handleWrapper.style.cursor = cursor;
          handleWrapper.appendChild(handleVisual);

          // Handles start hidden and reveal at 50% on hover. Full-visibility on
          // node selection is driven imperatively by selectNode/deselectNode
          // below (the vanilla ProseMirror node-view mechanism) — there is no
          // per-render `selected` prop here, unlike React/Vue node views.
          handleWrapper.classList.add("group-hover:opacity-50");

          handleWrapper.addEventListener("mousedown", (e) => {
            e.preventDefault();
            e.stopPropagation();

            const startX = e.clientX;
            const startWidth = imgContainer.offsetWidth;
            // FIX: Use getComputedStyle to get the actual content width of the parent, excluding padding/borders
            const styles = window.getComputedStyle(container.parentElement || editor.view.dom);
            const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
            const borderX =
              parseFloat(styles.borderLeftWidth) + parseFloat(styles.borderRightWidth);
            const parentWidth = (parseFloat(styles.width) || 800) - paddingX - borderX;

            const onMouseMove = (moveEvent: MouseEvent) => {
              const deltaX = moveEvent.clientX - startX;
              let newWidth = startWidth;

              if (position === "bottom-right" || position === "top-right") {
                newWidth = startWidth + deltaX;
              } else {
                newWidth = startWidth - deltaX;
              }

              newWidth = Math.max(50, newWidth);
              const widthPercent = (newWidth / parentWidth) * 100;
              imgContainer.style.width = `${Math.min(100, widthPercent)}%`;
            };

            const onMouseUp = () => {
              const finalWidth = imgContainer.style.width;
              if (typeof getPos === "function") {
                editor.view.dispatch(
                  editor.view.state.tr.setNodeAttribute(getPos(), "width", finalWidth),
                );
              }
              document.removeEventListener("mousemove", onMouseMove);
              document.removeEventListener("mouseup", onMouseUp);
            };

            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
          });

          return handleWrapper;
        };

        imgContainer.appendChild(createHandle("top-left"));
        imgContainer.appendChild(createHandle("top-right"));
        imgContainer.appendChild(createHandle("bottom-left"));
        imgContainer.appendChild(createHandle("bottom-right"));
      }

      container.appendChild(imgContainer);

      return {
        dom: container,
        contentDOM: null,
        update: (updatedNode) => {
          if (updatedNode.type !== node.type) return false;

          container.setAttribute("data-align", updatedNode.attrs.align);
          container.setAttribute("data-wrap", updatedNode.attrs.wrap);
          imgContainer.style.width = updatedNode.attrs.width;
          // Update margin on container, not img
          imgContainer.style.margin = updatedNode.attrs.margin || "0";
          img.style.setProperty("margin", "0", "important");

          return true;
        },
        selectNode: () => {
          imgContainer.classList.add("ProseMirror-selectednode");
          // Reveal the resize handles while the node is selected.
          const handles = imgContainer.querySelectorAll(".absolute");
          handles.forEach((h) => h.classList.remove("opacity-0"));
        },
        deselectNode: () => {
          imgContainer.classList.remove("ProseMirror-selectednode");
          const handles = imgContainer.querySelectorAll(".absolute");
          handles.forEach((h) => h.classList.add("opacity-0"));
        },
      };
    };
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, {
        "data-image-wrapper": "",
        "data-align": HTMLAttributes.align,
        "data-wrap": HTMLAttributes.wrap,
        style: `margin: ${HTMLAttributes.margin || "0"};`, // Apply margin to wrapper
      }),
      [
        "img",
        mergeAttributes(HTMLAttributes, {
          draggable: false,
          style: `width: ${HTMLAttributes.width}; height: auto; display: block; margin: 0;`, // No margin on img
        }),
      ],
    ];
  },
});
